import { alpha } from "@mui/material";
import { Z } from "../../../theme/zIndex";
import { Reservation } from "../types/Reservation";
import { format } from "date-fns";
import { es } from "date-fns/locale";

import { SxProps } from "@mui/material";
import { ReservaEstacionamiento } from "../../../types/ReservaEstacionamiento";
import {
  TOOLTIP_SURFACE_BG,
  TOOLTIP_SURFACE_RADIUS,
  TOOLTIP_SURFACE_SHADOW,
} from "../../shared/styles/tooltipSurface";
import { compareDateOnly, parseDateOnly } from "../../shared/utils/dateUtils";

// ------------------------------
//       COLORES DE RESERVAS
// ------------------------------
export const RESERVATION_COLORS = {
  CENTRO: {
    CONFIRMADA: "theme.palette.zonas.centro.border",
    ACEPTADA: "theme.palette.zonas.centro.border",
    PENDIENTE: "#ff9800",
    CANCELADA: "#f44336",
    DEFAULT: "#9e9e9e",
  },
  LOMA: {
    CONFIRMADA: "theme.palette.zonas.loma.border",
    ACEPTADA: "theme.palette.zonas.loma.border",
    PENDIENTE: "theme.palette.zonas.loma.border",
    CANCELADA: "#f44336",
    DEFAULT: "theme.palette.zonas.loma.border",
  },
};

// ------------------------------
//       DIMENSIONES Y ESPACIADO
// ------------------------------
export const RESERVATION_DIMENSIONS = {
  HEIGHT: "50px", // Aquí puedes modificar la altura de las barras de reserva
  BORDER_RADIUS: 15,
};

// ------------------------------
//       ESTILOS DEL CONTENEDOR
// ------------------------------
export const RESERVATION_CONTAINER_STYLES = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  padding: "2px 4px",
  opacity: 0.9,
  transition: "opacity 0.2s ease",
  "&:hover": {
    opacity: 1,
  },
};

// ------------------------------
//       ESTILOS DEL TEXTO
// ------------------------------
export const RESERVATION_TEXT_STYLES = {
  NOMBRE: {
    fontSize: "1rem",
    fontWeight: 700,
    color: "white",
    textAlign: "left",
    textShadow: "0 1px 2px rgba(0,0,0,0.2)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%",
  },
  PRECIO: {
    fontSize: "0.8rem",
    color: "white",
    textAlign: "left",
    textShadow: "0 1px 2px rgba(0,0,0,0.2)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%",
  },
};

// ------------------------------
//       ESTILOS DEL TOOLTIP
// ------------------------------

const TOOLTIP_BG = TOOLTIP_SURFACE_BG;
const TOOLTIP_SHADOW = TOOLTIP_SURFACE_SHADOW;
const TOOLTIP_RADIUS = TOOLTIP_SURFACE_RADIUS;
const TOOLTIP_WIDTH = 300;

const TOOLTIP_BASE_STYLES: SxProps = {
  bgcolor: TOOLTIP_BG,
  boxShadow: TOOLTIP_SHADOW,
  borderRadius: TOOLTIP_RADIUS,
  maxWidth: TOOLTIP_WIDTH,
  color: "#fff",
  willChange: "opacity, transform",
};

export const _getTooltipStyles = (
  position: { x: number; y: number },
  visible: boolean
): SxProps => ({
  ...TOOLTIP_BASE_STYLES,
  position: "fixed",
  left: position.x,
  top: position.y - 10,
  transform: `translateY(${visible ? "-100%" : "-90%"}) scale(${
    visible ? 1 : 0.95
  })`,
  transformOrigin: "bottom center",
  opacity: visible ? 1 : 0,
  transition: visible
    ? "transform 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)"
    : "none",
  pointerEvents: "none",
  zIndex: Z.tooltip,
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-8px",
    left: "50%",
    transform: "translateX(-50%)",
    borderLeft: "8px solid transparent",
    borderRight: "8px solid transparent",
    borderTop: `8px solid ${TOOLTIP_BG}`,
    transition: visible ? "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)" : "none",
    opacity: visible ? 1 : 0,
  },
});

// ------------------------------
//       FUNCIONES AUXILIARES
// ------------------------------

// Compatibilidad: las fechas de reserva se parsean en el helper compartido.
export const createUTCDate = (dateString: string): Date => {
  return parseDateOnly(dateString);
};

// Compatibilidad: compara solo año/mes/día.
export const compareUTCDates = (date1: Date, date2: Date): boolean => {
  return compareDateOnly(date1, date2) === 0;
};

// Función para obtener el color de la reserva
export const getReservationColor = (estado: string, departamento: string) => {
  const normalized = (estado || "").trim().toLowerCase();
  const esLoma = parseInt(departamento) > 18;
  const colors = esLoma ? RESERVATION_COLORS.LOMA : RESERVATION_COLORS.CENTRO;

  switch (normalized) {
    case "confirmada":
      return colors.CONFIRMADA;
    case "aceptada":
      return colors.ACEPTADA;
    case "pendiente":
      return colors.PENDIENTE;
    case "cancelada":
      return colors.CANCELADA;
    default:
      return colors.DEFAULT;
  }
};

export const calcularPosicionRangoPx = (
  entrada: string,
  salida: string,
  diasDelMes: Date[],
  dayColumnWidth: number,
  topPx: number
) => {
  const fechaEntrada = createUTCDate(entrada);
  const fechaSalida = createUTCDate(salida);

  const diasTotales = diasDelMes.length;

  const startIndex = diasDelMes.findIndex((dia) =>
    compareUTCDates(dia, fechaEntrada)
  );
  const endIndex = diasDelMes.findIndex((dia) =>
    compareUTCDates(dia, fechaSalida)
  );

  const startsOutsideMonth = startIndex === -1;
  const endsOutsideMonth = endIndex === -1;

  const safeStartIndex = startsOutsideMonth ? 0 : startIndex;
  const safeEndIndex = endsOutsideMonth ? diasTotales - 1 : endIndex;

  const leftOffset = startsOutsideMonth ? 0 : 0.5;
  const rightOffset = endsOutsideMonth ? 0 : 0.5;

  const leftPx = (safeStartIndex + leftOffset) * dayColumnWidth;
  const widthPx =
    (safeEndIndex - safeStartIndex + 1) * dayColumnWidth -
    (leftOffset + rightOffset) * dayColumnWidth;

  return {
    leftPx,
    widthPx,
    topPx,
    startsOutsideMonth,
    endsOutsideMonth,
  };
};

// Función para calcular la posición de una reserva en píxeles
export const calcularPosicionReservaPx = (
  reserva: Reservation,
  diasDelMes: Date[],
  dayColumnWidth: number,
  rowHeight: number,
  topPx: number
) => calcularPosicionRangoPx(
  reserva.entrada,
  reserva.salida,
  diasDelMes,
  dayColumnWidth,
  topPx
);

// Función para calcular la posición de una reserva de ESTACIONAMIENTO en píxeles.
// Usa la misma lógica visual que las reservas de departamento:
// la salida es el día de retiro, no una noche extra ocupada.
export const calcularPosicionReservaEstacionamientoPx = (
  reserva: ReservaEstacionamiento,
  diasDelMes: Date[],
  dayColumnWidth: number,
  rowHeight: number,
  topPx: number
) => calcularPosicionRangoPx(
  reserva.entrada,
  reserva.salida,
  diasDelMes,
  dayColumnWidth,
  topPx
);

// Función para obtener el estilo base de una barra de reserva
export const getReservationBlockVisualStyle = (
  startsOutsideMonth: boolean,
  endsOutsideMonth: boolean,
  color: string
) => {
  const blockStyle = {
    background: `linear-gradient(135deg, ${color} 0%, ${alpha(
      color,
      0.8
    )} 100%)`,
    borderTopLeftRadius: startsOutsideMonth
      ? 0
      : RESERVATION_DIMENSIONS.BORDER_RADIUS,
    borderBottomLeftRadius: startsOutsideMonth
      ? 0
      : RESERVATION_DIMENSIONS.BORDER_RADIUS,
    borderTopRightRadius: endsOutsideMonth
      ? 0
      : RESERVATION_DIMENSIONS.BORDER_RADIUS,
    borderBottomRightRadius: endsOutsideMonth
      ? 0
      : RESERVATION_DIMENSIONS.BORDER_RADIUS,
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    cursor: "pointer",
    zIndex: 1,
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    },
  };

  if (startsOutsideMonth || endsOutsideMonth) {
    return {
      ...blockStyle,
      borderLeft: startsOutsideMonth ? "2px dashed white" : undefined,
      borderRight: endsOutsideMonth ? "2px dashed white" : undefined,
    };
  }

  return blockStyle;
};

export const getReservationBaseStyle = (
  leftPx: number,
  widthPx: number,
  topPx: number,
  startsOutsideMonth: boolean,
  endsOutsideMonth: boolean,
  color: string
) => {
  const baseStyle = {
    ...getReservationBlockVisualStyle(
      startsOutsideMonth,
      endsOutsideMonth,
      color
    ),
    position: "absolute" as const,
    left: `${leftPx}px`,
    top: `${topPx}px`,
    width: `${widthPx}px`,
    height: RESERVATION_DIMENSIONS.HEIGHT,
  };

  return baseStyle;
};

// Función para formatear las fechas de una reserva
export const formatReservaFechas = (entrada: string, salida: string) => {
  const fechaEntrada = createUTCDate(entrada);
  const fechaSalida = createUTCDate(salida);

  return {
    entrada: {
      fecha: format(fechaEntrada, "dd/MM/yyyy"),
      dia: format(fechaEntrada, "EEE", { locale: es }),
    },
    salida: {
      fecha: format(fechaSalida, "dd/MM/yyyy"),
      dia: format(fechaSalida, "EEE", { locale: es }),
    },
  };
};

// Función para obtener el color según el estado
interface EstadoColorTheme {
  palette: {
    zonas: {
      centro: {
        border: string;
      };
    };
  };
}

export const getEstadoColor = (estado: string, theme?: EstadoColorTheme): string => {
  const normalized = (estado || "").trim().toLowerCase();
  switch (normalized) {
    case "confirmada":
      return theme ? theme.palette.zonas.centro.border : "#99cc00";
    case "aceptada":
      return theme ? theme.palette.zonas.centro.border : "#99cc00";
    case "pendiente":
      return "#FFC107";
    default:
      return "#9C27B0";
  }
};
